import type { CamposDeLaFila, InstalacionDeInforme } from "@/lib/informes";
import { textoDeDecimal, textoDeNumero } from "@/lib/montos";
import { numeroEscrito } from "@/lib/numeroEscrito";

/* ---------------------------------------------------------------------------
   La pestaña "Instalaciones y obras complementarias": traducción formulario ↔
   API.

   VIVE APARTE DEL COMPONENTE por lo mismo que su gemelo de maquinaria: acá
   está el riesgo real de la pantalla, que es el punto de miles. Un "1.000"
   leído como decimal deja la fila perfectamente formada con una losa de un
   metro cuadrado, en un informe pericial.
--------------------------------------------------------------------------- */

/**
 * Una instalación mientras se escribe.
 *
 * TODO ES STRING porque eso es lo que hay en un `<input>`: guardarlo como
 * número obligaría a decidir qué es un campo a medio teclear ("-", "1,") y la
 * respuesta siempre sería borrar lo que la persona está escribiendo.
 *
 * NO HAY VIDA ÚTIL NI AJUSTE, y esa es la diferencia de fondo con maquinaria y
 * construcciones: los dos valores en UF se cargan derecho y son independientes
 * entre sí.
 */
export interface FormularioDeInstalacion {
  tipo: string;
  anio: string;
  cantidad: string;
  valor_reposicion_uf_un: string;
  valor_comercial_uf_un: string;
  nota_terreno: string;
}

/**
 * El formulario recién abierto.
 *
 * NINGÚN CAMPO ARRANCA CON VALOR, al revés que maquinaria —donde `unidades`
 * empieza en 1 y la moneda en USD—: acá el request no exige más que el tipo, y
 * la cantidad de una obra no tiene un "1" razonable que suponer. Se mide, no
 * se cuenta.
 */
export const INSTALACION_VACIA: FormularioDeInstalacion = {
  tipo: "",
  anio: "",
  cantidad: "",
  valor_reposicion_uf_un: "",
  valor_comercial_uf_un: "",
  nota_terreno: "",
};

/** Carga el formulario con una instalación ya guardada, para corregirla. */
export function formularioDesdeInstalacion(
  instalacion: InstalacionDeInforme,
): FormularioDeInstalacion {
  return {
    tipo: instalacion.tipo,
    anio: textoDeNumero(instalacion.anio),
    // Los decimales llegan como "1000.00": se muestran sin el relleno de ceros
    // y con coma. Con el punto crudo, reabrir la fila y guardarla sin tocar
    // nada cambiaría su cantidad.
    cantidad: textoDeDecimal(instalacion.cantidad),
    valor_reposicion_uf_un: textoDeDecimal(instalacion.valor_reposicion_uf_un),
    valor_comercial_uf_un: textoDeDecimal(instalacion.valor_comercial_uf_un),
    nota_terreno: instalacion.nota_terreno ?? "",
  };
}

/**
 * El cuerpo que viaja al servidor.
 *
 * Un campo vacío se manda como `null` y NO se omite: omitirlo en un PATCH
 * significa "no lo toques" (el request usa `sometimes`), así que vaciar un
 * valor no borraría nada. Son dos intenciones distintas y la pantalla ofrece
 * las dos.
 */
export function camposDesdeInstalacion(formulario: FormularioDeInstalacion): CamposDeLaFila {
  return {
    tipo: formulario.tipo.trim(),
    anio: numeroEscrito(formulario.anio),
    cantidad: numeroEscrito(formulario.cantidad),
    valor_reposicion_uf_un: numeroEscrito(formulario.valor_reposicion_uf_un),
    valor_comercial_uf_un: numeroEscrito(formulario.valor_comercial_uf_un),
    nota_terreno: formulario.nota_terreno.trim() === "" ? null : formulario.nota_terreno.trim(),
  };
}
